import { feature as topoFeature } from "topojson-client";
import { parsePCON24CDs } from "./helpers";
import {
  LEVEL_CONSTITUENCY,
  LEVEL_LOCAL_AUTHORITY,
  LEVEL_WARD,
  levelGeometryUrl,
  levelIdKey,
  resolveLocalAuthorityCode,
} from "../geoLevels";

// LRU cache: "level:parentAreaCode" → { geo, areas }
const _geoCache = new Map();
const _geoCacheMaxSize = 10;

// Raw-features cache: level → Feature[]
// Populated by background preload so drill-ins never re-download the FGB file.
const _rawFeaturesCache = new Map();

// In-flight deduplication: level → Promise<Feature[]>
// Concurrent calls for the same level share one fetch instead of making N copies.
const _geoInflight = new Map();

export async function loadGeo({
  level,
  // The area drilled into, when `level` is a drill-down. Named for the
  // constituency it used to always be; a local-authority code is equally valid.
  constituencyCode: parentCode = null,
  doFit = true,
  bounds = null,
  backgroundOnly = false,
  updateAreas = true,
  setState,
  mapRef,
  fitToGeo,
  normalizeId,
  handlePossibleAreasChange,
}) {
  const cacheKey = `${level}:${parentCode || ""}`;

  // ── Cache hit ────────────────────────────────────────────────────────
  const cached = _geoCache.get(cacheKey);
  if (cached) {
    if (backgroundOnly) return;
    if (updateAreas && typeof handlePossibleAreasChange === "function") {
      handlePossibleAreasChange(cached.areas);
    }
    setState({ geoData: cached.geo, styledGeo: null }, () => {
      if (!doFit) return;
      const map = mapRef.current?.getMap();
      if (!map) return;
      if (bounds) { map.fitBounds(bounds, { padding: 24, duration: 500 }); return; }
      if (!parentCode) {
        map.fitBounds([[-8.5, 49.8], [1.8, 60.9]], { padding: 20, duration: 500 });
      } else {
        fitToGeo(cached.geo);
      }
    });
    return;
  }

  // ── Determine URL ────────────────────────────────────────────────────
  const url = levelGeometryUrl(level);
  if (!url) {
    console.error(`[loadGeo] Unsupported level: ${level}`);
    return;
  }

  try {
    // ── In-flight deduplication ──────────────────────────────────────────
    // If another call is already fetching this level, await its promise
    // instead of making a second download. All callers share the result.
    let rawFeatures;
    if (_rawFeaturesCache.has(level)) {
      rawFeatures = _rawFeaturesCache.get(level);
    } else if (_geoInflight.has(level)) {
      rawFeatures = await _geoInflight.get(level);
    } else {
      const fetchPromise = (async () => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        // TopoJSON, not GeoJSON: shared borders are stored once and
        // coordinates are delta-encoded on a sub-metre grid, which makes
        // these files ~4x smaller over the wire than the FlatGeobuf they
        // replaced. The geometry itself is unsimplified — see
        // scripts/build-geo.mjs. topoFeature expands it back to GeoJSON.
        const topology = await response.json();
        const objectName = Object.keys(topology.objects || {})[0];
        if (!objectName) throw new Error(`TopoJSON has no objects: ${url}`);
        return topoFeature(topology, topology.objects[objectName]).features;
      })();

      _geoInflight.set(level, fetchPromise);
      try {
        rawFeatures = await fetchPromise;
        _rawFeaturesCache.set(level, rawFeatures);
      } finally {
        _geoInflight.delete(level);
      }
    }

    // ── Filter ward features by their parent area ────────────────────────
    // `parentCode` is whichever area was drilled into — a constituency or a
    // local authority. Their GSS code spaces are disjoint (E14/W07/S14 for
    // seats, E06-E09/W06/S12 for districts), so testing both parents is
    // unambiguous and saves every caller having to say which kind it holds.
    let features = rawFeatures;
    if (level === LEVEL_WARD && parentCode) {
      const code = String(parentCode);
      features = rawFeatures.filter((f) => {
        const props = f.properties;
        if (parsePCON24CDs(props?.PCON24CD).includes(code)) return true;
        return resolveLocalAuthorityCode(props?.LAD23CD) === code;
      });
    }

    // ── Normalise IDs ────────────────────────────────────────────────────
    // Every district code a feature carries — the local-authority layer's own,
    // and the parent district on a ward — is rewritten to the code the survey
    // data uses, so nothing downstream has to know which of the six reissued
    // codes need resolving. It matters because the Mapbox layer filters and
    // the chat highlight expressions read these properties straight off the
    // feature and have to agree with the codes the API comes back on.
    const idKey = levelIdKey(level);
    const geo = {
      type: "FeatureCollection",
      features: features.map((f) => {
        const id = normalizeId(level, f.properties);
        return {
          ...f,
          properties: {
            ...f.properties,
            [idKey]: id,
            ...(level === LEVEL_WARD && f.properties?.LAD23CD
              ? { LAD23CD: resolveLocalAuthorityCode(f.properties.LAD23CD) }
              : {}),
            area_cd: id,
            area_id: id,
          },
        };
      }),
    };

    // ── Build PossibleAreas ──────────────────────────────────────────────
    let possibleAreas = [];
    if (level === LEVEL_CONSTITUENCY) {
      possibleAreas = geo.features
        .filter((f) => f.properties.PCON24NM)
        .map((f) => ({ name: f.properties.PCON24NM, area_cd: f.properties.area_cd, level: LEVEL_CONSTITUENCY }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } else if (level === LEVEL_WARD) {
      possibleAreas = geo.features
        .map((f) => ({ name: f.properties.WD24NM, area_cd: f.properties.area_cd, constituency: parsePCON24CDs(f.properties.PCON24CD)[0] ?? null, level: LEVEL_WARD }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } else if (level === LEVEL_LOCAL_AUTHORITY) {
      possibleAreas = geo.features
        .filter((f) => f.properties.LAD23NM)
        .map((f) => ({ name: f.properties.LAD23NM, area_cd: f.properties.area_cd, level: LEVEL_LOCAL_AUTHORITY }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    // ── Write to LRU cache ───────────────────────────────────────────────
    if (_geoCache.size >= _geoCacheMaxSize) {
      _geoCache.delete(_geoCache.keys().next().value);
    }
    _geoCache.set(cacheKey, { geo, areas: possibleAreas });

    if (updateAreas && typeof handlePossibleAreasChange === "function") {
      handlePossibleAreasChange(possibleAreas);
    }

    if (backgroundOnly) return;

    setState({ geoData: geo, styledGeo: null }, () => {
      if (!doFit) return;
      const map = mapRef.current?.getMap();
      if (!map) return;
      if (bounds) { map.fitBounds(bounds, { padding: 24, duration: 500 }); return; }
      if (!parentCode) {
        map.fitBounds([[-8.5, 49.8], [1.8, 60.9]], { padding: 20, duration: 500 });
      } else {
        fitToGeo(geo);
      }
    });
  } catch (error) {
    console.error("[loadGeo] Shape load failed:", error);
  }
}
